import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ChevronUp, ChevronDown, ChevronsUpDown, Download, Loader2, Inbox } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { DashboardHeader } from "@/components/DashboardHeader";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function SortIcon({ col, sortCol, sortDir }) {
  if (sortCol !== col) return <ChevronsUpDown className="w-3 h-3 opacity-30" />;
  return sortDir === "asc" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />;
}

function fmtTimer(h) {
  if (h === null || h === undefined) return "—";
  const neg = h < 0;
  const abs = Math.abs(h);
  const hh = Math.floor(abs);
  const mm = Math.round((abs - hh) * 60);
  const str = mm > 0 ? `${hh}t ${mm}m` : `${hh}t`;
  return neg ? `-${str}` : str;
}

export default function FlexPage() {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();

  const [flex, setFlex] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sortCol, setSortCol] = useState("navn");
  const [sortDir, setSortDir] = useState("asc");
  const [fraDato, setFraDato] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d.toISOString().slice(0, 10);
  });
  const [tilDato, setTilDato] = useState(() => new Date().toISOString().slice(0, 10));
  const [eksportLoading, setEksportLoading] = useState(false);

  const handleLogout = () => { logout(); navigate("/login"); };

  useEffect(() => {
    if (!user || !["superadmin", "owner", "api_admin"].includes(user.role)) { navigate("/"); return; }
    setLoading(true);
    axios.get(`${API}/admin/flex`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setFlex(r.data.flex || r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user, navigate, token]);

  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir("asc"); }
  };

  const filtered = useMemo(() => flex.filter(f => {
    if (!search) return true;
    return (f.navn || "").toLowerCase().includes(search.toLowerCase());
  }), [flex, search]);

  const sorted = useMemo(() => [...filtered].sort((a, b) => {
    const av = a[sortCol] ?? "";
    const bv = b[sortCol] ?? "";
    if (typeof av === "number" && typeof bv === "number") {
      return sortDir === "asc" ? av - bv : bv - av;
    }
    return sortDir === "asc"
      ? String(av).localeCompare(String(bv), "da")
      : String(bv).localeCompare(String(av), "da");
  }), [filtered, sortCol, sortDir]);

  const handleEksport = async () => {
    setEksportLoading(true);
    try {
      const r = await fetch(`${API}/admin/payroll-eksport?format=csv&fra=${fraDato}&til=${tilDato}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `payroll-${fraDato}-${tilDato}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { alert("Eksport fejlede"); }
    setEksportLoading(false);
  };

  const Th = ({ col, label }) => (
    <th className="text-left py-3 px-4 text-[13px] font-semibold text-muted-foreground uppercase tracking-wider cursor-pointer select-none hover:text-foreground transition-colors"
      onClick={() => handleSort(col)}>
      <span className="flex items-center gap-1">{label}<SortIcon col={col} sortCol={sortCol} sortDir={sortDir} /></span>
    </th>
  );

  return (
    <div className="min-h-screen bg-background text-foreground">
      <DashboardHeader user={user} onLogout={handleLogout} />

      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold tracking-tight mb-1">Flex-saldo</h1>
            <p className="text-sm text-muted-foreground">Oversigt over alle medarbejderes flex</p>
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-3 mb-5">
          <div>
            <label className="text-[13px] text-muted-foreground mb-1 block">Søg medarbejder</label>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Navn..."
              className="bg-secondary border border-border rounded-lg px-3 h-8 text-sm w-48 focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]" />
          </div>
        </div>

        {loading ? (
          <div className="flex flex-col items-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            <p className="text-[13px] text-muted-foreground mt-3">Henter flex-saldoer...</p>
          </div>
        ) : sorted.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <Inbox className="w-10 h-10 mb-3 opacity-30" />
            <p className="text-sm">Ingen data fundet</p>
          </div>
        ) : (
          <div className="rounded-xl border border-border overflow-hidden mb-6">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-secondary/50">
                  <Th col="navn" label="Medarbejder" />
                  <Th col="timer_pr_uge" label="Timer/uge" />
                  <Th col="forventede_timer" label="Forventede timer" />
                  <Th col="faktiske_timer" label="Faktiske timer" />
                  <Th col="flex_saldo" label="Flex-saldo" />
                </tr>
              </thead>
              <tbody>
                {sorted.map((f, i) => {
                  const saldo = parseFloat(f.flex_saldo || 0);
                  return (
                    <tr key={f.medarbejder_id || f.navn}
                      className={`border-b border-border hover:bg-secondary/30 transition-colors ${i % 2 === 0 ? "" : "bg-secondary/10"}`}>
                      <td className="py-3 px-4 font-medium">{f.navn || "—"}</td>
                      <td className="py-3 px-4 text-muted-foreground tabular-nums">{f.timer_pr_uge ? `${f.timer_pr_uge}t` : "—"}</td>
                      <td className="py-3 px-4 tabular-nums">{fmtTimer(f.forventede_timer)}</td>
                      <td className="py-3 px-4 tabular-nums">{fmtTimer(f.faktiske_timer)}</td>
                      <td className="py-3 px-4">
                        <span className={`tabular-nums font-semibold ${saldo >= 0 ? "text-green-500" : "text-red-500"}`}>
                          {saldo >= 0 ? "+" : ""}{fmtTimer(saldo)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="rounded-xl border border-border p-5">
          <h3 className="text-[13px] font-bold text-muted-foreground uppercase tracking-wider mb-4">Eksporter lønkørsel til CSV</h3>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="text-[13px] text-muted-foreground mb-1 block">Fra dato</label>
              <input type="date" value={fraDato} onChange={e => setFraDato(e.target.value)}
                className="bg-secondary border border-border rounded-lg px-3 h-8 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]" />
            </div>
            <div>
              <label className="text-[13px] text-muted-foreground mb-1 block">Til dato</label>
              <input type="date" value={tilDato} onChange={e => setTilDato(e.target.value)}
                className="bg-secondary border border-border rounded-lg px-3 h-8 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]" />
            </div>
            <button onClick={handleEksport} disabled={eksportLoading}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 transition-colors disabled:opacity-60">
              {eksportLoading
                ? <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Eksporterer...</>
                : <><Download className="w-4 h-4" /> Eksporter til CSV</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
